import { AddIncidentPayload } from '@collector/desktop-feature-incidents-queue-core'
import { findSubParticipantWithStat } from '@collector/shared-domain'
import {
  BaseballEventDetailId,
  BaseballEventDetailIdHalfValues,
  BaseballIncidentId,
  BaseballSubParticipantStatId,
  EventStatusId,
  Participant,
} from '@collector/sportsapi-client-legacy'
import { SubParticipant } from '@collector/sportsapi-types'

import {
  IncidentQueuePayloadConfigMetadata,
  SubParticipantType,
} from '../../../configuration'
import { getPitchingBatting } from '../../utils'

type ParticipantTeam = 'batting' | 'pitching'

export function getThrowPitcherPayload(
  participant: Participant,
  status_id?: EventStatusId,
): AddIncidentPayload {
  return {
    status_id,
    incident_id: BaseballIncidentId.ThrowPitcher,
    participant_team_id: participant.id,
    minute: 0,
    second: 0,
  }
}

export function setSubParticipant(
  subParticipant: SubParticipant | null,
  type: SubParticipantType,
  payload: AddIncidentPayload,
): void {
  if (!subParticipant) return

  const name = subParticipant.short_name
  const id = subParticipant.id

  switch (type) {
    case 'participant':
      payload.participant_id = id
      payload.participant_name = name
      break
    case 'assistant':
      payload.assistant_id = id
      payload.assistant_name = name
      break
    case 'secondary_assistant':
      payload.secondary_assistant_id = id
      payload.secondary_assistant_name = name
      break
    default:
      throw new Error(`Unsupported subparticipant type: ${type}`)
  }
}

export function setParticipantTeamId(
  participant: Participant,
  payload: AddIncidentPayload,
): void {
  payload.participant_team_id = participant.id
}

function getBattingPitchingContext(
  {
    getEventDetailValue,
    homeParticipant,
    awayParticipant,
    homeSubParticipants,
    awaySubParticipants,
    eventSettings,
  }: IncidentQueuePayloadConfigMetadata,
  payloadParticipantTeamId: number | undefined,
  participantTeam: ParticipantTeam,
): {
  battingParticipant: Participant
  pitchingParticipant: Participant
  battingSubparticipants: SubParticipant[]
  pitchingSubparticipants: SubParticipant[]
} {
  const invertedParticipants = eventSettings.invertedParticipants
  // manual add incident
  //
  // If payload has participant_team_id, use it to determine batting/pitching participants
  if (payloadParticipantTeamId !== undefined) {
    const [actualHome, actualAway, actualHomeSubs, actualAwaySubs] =
      invertedParticipants
        ? [
            awayParticipant,
            homeParticipant,
            awaySubParticipants,
            homeSubParticipants,
          ]
        : [
            homeParticipant,
            awayParticipant,
            homeSubParticipants,
            awaySubParticipants,
          ]
    const isHomeTeam = payloadParticipantTeamId === actualHome.id
    const participant = isHomeTeam ? actualHome : actualAway
    const oppositeParticipant = isHomeTeam ? actualAway : actualHome
    const participantSubs = isHomeTeam ? actualHomeSubs : actualAwaySubs
    const oppositeParticipantSubs = isHomeTeam ? actualAwaySubs : actualHomeSubs

    // Assign batting/pitching roles based on participantTeam
    const battingParticipant =
      participantTeam === 'batting' ? participant : oppositeParticipant
    const pitchingParticipant =
      participantTeam === 'pitching' ? participant : oppositeParticipant
    const battingSubparticipants =
      participantTeam === 'batting' ? participantSubs : oppositeParticipantSubs
    const pitchingSubparticipants =
      participantTeam === 'pitching' ? participantSubs : oppositeParticipantSubs

    return {
      battingParticipant,
      pitchingParticipant,
      battingSubparticipants,
      pitchingSubparticipants,
    }
  }

  // no payload participant_team_id, regular flow
  // use eventDetailHalfValue to determine batting/pitching
  const eventDetailHalfValue = getEventDetailValue(
    BaseballEventDetailId.Half,
  ) as BaseballEventDetailIdHalfValues

  const [pitchingParticipant, battingParticipant] = invertedParticipants
    ? getPitchingBatting(eventDetailHalfValue, awayParticipant, homeParticipant)
    : getPitchingBatting(eventDetailHalfValue, homeParticipant, awayParticipant)

  const [pitchingSubparticipants, battingSubparticipants] = invertedParticipants
    ? getPitchingBatting(
        eventDetailHalfValue,
        awaySubParticipants,
        homeSubParticipants,
      )
    : getPitchingBatting(
        eventDetailHalfValue,
        homeSubParticipants,
        awaySubParticipants,
      )

  return {
    battingParticipant,
    pitchingParticipant,
    battingSubparticipants,
    pitchingSubparticipants,
  }
}

export function getAddIncidentContext(
  metadata: IncidentQueuePayloadConfigMetadata,
  participantTeam: ParticipantTeam,
  payload: AddIncidentPayload,
): {
  currentBatter: SubParticipant | null
  currentPitcher: SubParticipant | null
  participant: Participant
  oppositeParticipant: Participant
} {
  const {
    battingParticipant,
    pitchingParticipant,
    battingSubparticipants,
    pitchingSubparticipants,
  } = getBattingPitchingContext(
    metadata,
    payload.participant_team_id,
    participantTeam,
  )

  const currentBatter = findSubParticipantWithStat(
    battingSubparticipants,
    BaseballSubParticipantStatId.CurrentBatter,
  )

  const currentPitcher = findSubParticipantWithStat(
    pitchingSubparticipants,
    BaseballSubParticipantStatId.CurrentPitcher,
  )

  const isBattingTeam = participantTeam === 'batting'
  const participant = isBattingTeam ? battingParticipant : pitchingParticipant
  const oppositeParticipant = isBattingTeam
    ? pitchingParticipant
    : battingParticipant

  return {
    currentBatter,
    currentPitcher,
    participant,
    oppositeParticipant,
  }
}
